import type { DocMeta } from "./types";

const SLUG = "hipaa-compliance";
const id = (s: string) => `${SLUG}-${s}`;

export const HIPAA_SECURITY_META: DocMeta = {
  slug: SLUG,
  title: "HIPAA & Security Statement",
  subtitle:
    "Logical Health's role under HIPAA, our safeguards, and how privacy requests are handled",
  description:
    "Logical Health's role under HIPAA, our safeguards, incident response, and how privacy requests are routed.",
  lastUpdated: "May 6, 2026",
  sections: [
    { id: id("introduction"), title: "Introduction" },
    { id: id("our-role-under-hipaa"), title: "1. Our Role under HIPAA" },
    {
      id: id("administrative-safeguards"),
      title: "2. Administrative Safeguards",
    },
    { id: id("physical-safeguards"), title: "3. Physical Safeguards" },
    { id: id("technical-safeguards"), title: "4. Technical Safeguards" },
    {
      id: id("incident-breach-response"),
      title: "5. Incident & Breach Response",
    },
    {
      id: id("business-associate-agreements"),
      title: "6. Business Associate Agreements",
    },
    {
      id: id("subcontractors-and-vendors"),
      title: "7. Subcontractors and Vendors",
    },
    {
      id: id("individual-rights-and-privacy-requests"),
      title: "8. Individual Rights and Privacy Requests",
    },
    {
      id: id("changes-to-this-statement"),
      title: "9. Changes to This Statement",
    },
    { id: id("contact"), title: "10. Contact" },
  ],
};

export function HipaaSecurity() {
  return (
    <>
      <section id={id("introduction")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction</h2>
        <p>
          Logical Health, Inc. (&ldquo;Logical Health&rdquo;,
          &ldquo;we&rdquo;, &ldquo;us&rdquo;, and &ldquo;our&rdquo;) provides
          healthcare technology and member-support services. In customer
          deployments involving Protected Health Information (&ldquo;PHI&rdquo;)
          or electronic Protected Health Information (&ldquo;ePHI&rdquo;),
          Logical Health may act as a Business Associate to Covered Entity
          clients. In that role, Logical Health processes PHI/ePHI only as
          permitted by the applicable Business Associate Agreement
          (&ldquo;BAA&rdquo;), customer instructions, HIPAA/HITECH, and
          applicable law.
        </p>
        <p className="mt-6">
          <strong>
            This page is a summary, not a Notice of Privacy Practices.
          </strong>{" "}
          This page summarizes Logical Health&rsquo;s HIPAA-oriented
          safeguards and compliance posture. It is not a Covered Entity
          Notice of Privacy Practices and does not replace any health plan,
          provider, employer-plan, or customer privacy notice.
        </p>
      </section>

      <section id={id("our-role-under-hipaa")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          1. Our Role under HIPAA
        </h2>
        <p>
          When Logical Health acts as a Business Associate, our handling of
          PHI/ePHI is limited to the permitted uses and disclosures set out in
          the applicable BAA, customer instructions, HIPAA/HITECH, and law. We
          do not act as a Covered Entity for client-controlled health
          information processed on behalf of a customer, and we are not a
          treatment provider, health plan, or healthcare clearinghouse for that
          information.
        </p>
        <p className="mt-4">
          Our public website and marketing pages are governed by our{" "}
          <a
            href="/legal#privacy-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="/legal#terms-of-service"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Terms of Service
          </a>
          . Cookies and similar technologies are described in our{" "}
          <a
            href="/cookie-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Cookie Policy
          </a>
          . Messaging programs are described in our{" "}
          <a
            href="/messaging-terms"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Messaging Terms
          </a>
          .
        </p>
      </section>

      <section id={id("administrative-safeguards")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          2. Administrative Safeguards
        </h2>
        <p>
          Logical Health maintains administrative safeguards appropriate to its
          size, complexity, and risk profile, including:
        </p>
        <ul className="ml-6 space-y-2">
          <li>Documented information-security and privacy policies.</li>
          <li>
            Workforce HIPAA, security, and privacy training, with
            role-appropriate updates.
          </li>
          <li>Role-based access management and periodic access review.</li>
          <li>
            Risk analysis, risk-treatment planning, and security incident
            procedures.
          </li>
          <li>
            Risk-based monitoring, alerting, access review, and evidence
            review.
          </li>
          <li>Contingency planning and recovery preparation.</li>
        </ul>
      </section>

      <section id={id("physical-safeguards")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          3. Physical Safeguards
        </h2>
        <p>
          Logical Health does not operate its own data centers. Cloud
          infrastructure providers used to deliver our services maintain
          physical data-center controls under their respective compliance
          programs. Workforce devices used to access PHI/ePHI are subject to
          endpoint-management and workstation-security expectations.
        </p>
      </section>

      <section id={id("technical-safeguards")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          4. Technical Safeguards
        </h2>
        <p>
          Logical Health uses session-management, identity, encryption,
          logging, and endpoint safeguards appropriate to the system and the
          data:
        </p>
        <ul className="ml-6 space-y-2">
          <li>
            Encryption of PHI/ePHI in transit using modern TLS where supported
            by the channel.
          </li>
          <li>
            Encryption of PHI/ePHI at rest using industry-standard algorithms
            in supported services.
          </li>
          <li>
            Identity, authentication, and access controls scaled to the
            sensitivity of the system, including multi-factor authentication
            for administrative access where supported.
          </li>
          <li>
            Audit logging and integrity controls for systems that handle
            PHI/ePHI, scoped to what is appropriate for the platform.
          </li>
        </ul>
      </section>

      <section id={id("incident-breach-response")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          5. Incident &amp; Breach Response
        </h2>
        <p>
          If Logical Health discovers a potential breach of unsecured PHI
          involving PHI/ePHI processed on behalf of a Covered Entity client,
          Logical Health will investigate, mitigate as appropriate, preserve
          relevant evidence, and notify the applicable Covered Entity as
          required by HIPAA, the applicable BAA, and law. Where Logical Health
          has a direct legal or contractual notice obligation, Logical Health
          will support or provide required notices in accordance with
          applicable law and contract.
        </p>
      </section>

      <section id={id("business-associate-agreements")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          6. Business Associate Agreements
        </h2>
        <p>
          When Logical Health acts as a Business Associate, we enter into
          appropriate Business Associate Agreements that govern permitted uses
          and disclosures, safeguards, breach notification, subcontractor
          obligations, and termination, return, or destruction of PHI at the
          end of the engagement.
        </p>
      </section>

      <section id={id("subcontractors-and-vendors")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          7. Subcontractors and Vendors
        </h2>
        <p>
          Subcontractors that create, receive, maintain, or transmit PHI/ePHI
          on Logical Health&rsquo;s behalf are subject to appropriate
          contractual safeguards, including BAAs where required by HIPAA.
          Vendors that process sensitive data are reviewed under Logical
          Health&rsquo;s vendor-management process and, where appropriate,
          covered by data protection agreements, contractual safeguards, or
          assurance evidence.
        </p>
      </section>

      <section
        id={id("individual-rights-and-privacy-requests")}
        className="mb-12"
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          8. Individual Rights and Privacy Requests
        </h2>
        <p>
          Individuals should generally direct HIPAA rights requests involving
          health-plan, provider, claims, eligibility, benefits, or other
          client-controlled PHI to the applicable Covered Entity, such as the
          relevant health plan or provider. Where a Covered Entity customer
          has delegated a request to Logical Health or a contract authorizes
          Logical Health to assist directly, Logical Health supports the
          request in accordance with the applicable agreement, HIPAA/HITECH,
          and law.
        </p>
        <p className="mt-4">
          For privacy requests involving information that you provided
          directly to Logical Health through our public website, see the{" "}
          <a
            href="/legal#privacy-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Privacy Policy
          </a>{" "}
          and contact{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>
          .
        </p>
      </section>

      <section id={id("changes-to-this-statement")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          9. Changes to This Statement
        </h2>
        <p>
          Logical Health may update this statement from time to time to reflect
          changes in our practices, technologies, customer relationships, legal
          requirements, or compliance posture. When we make changes, we will
          post the updated statement on this page and update the &ldquo;Last
          Updated&rdquo; date.
        </p>
      </section>

      <section id={id("contact")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Contact</h2>
        <p>
          For HIPAA, security, privacy, breach, compliance, or related
          inquiries, contact us at:
        </p>
        <p className="mb-2 mt-4">
          <strong>Email:</strong>{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>
        </p>
        <p className="mb-2">
          <strong>Subject Line:</strong> HIPAA &amp; Security Inquiry
        </p>
      </section>
    </>
  );
}
